import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ChevronDown, ChevronUp, Save, MessageSquare, Link2, Edit2, Loader2, Check, FileSpreadsheet, FileText, Download, User, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import TrainerGuideModal from '../training/TrainerGuideModal';

// ─── Export Utilities ────────────────────────────────────────
// Only render the "open link" anchor for mock-interview links that are safe
// http(s) URLs. Blocks javascript:, data:, mailto:, etc. and swallows malformed
// strings so we never render a phishy anchor.
const isSafeLink = (v) => {
  if (!v || typeof v !== 'string') return false;
  try {
    const u = new URL(v.trim());
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};



function exportToExcel(rows, columns, filename) {
  const data = rows.map(r => {
    const obj = {};
    columns.forEach(c => { obj[c.label] = c.render ? c.render(r) : r[c.key]; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function exportToPdf(rows, columns, title) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 18);
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 24);
  autoTable(doc, {
    startY: 28,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => c.render ? c.render(r) : (r[c.key] ?? '-'))),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [109, 40, 217] },
  });
  doc.save(`${title.replace(/\s/g, '_')}.pdf`);
}

// ─── Editable Cells (same as admin report) ───────────────────

function EditableCell({ value, field, batchId, studentId, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setVal(value || ''); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = async () => {
    setEditing(false);
    if (val !== (value || '')) {
      await onSave(batchId, studentId, { [field]: val });
    }
  };

  if (!editing) {
    return (
      <div className="cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded min-h-[24px] text-xs text-slate-600 group flex items-center gap-1"
        onClick={() => setEditing(true)} title="Click to edit">
        {val || <span className="text-slate-300 italic">-</span>}
        <Edit2 className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100" />
      </div>
    );
  }
  return (
    <Input ref={ref} value={val} onChange={e => setVal(e.target.value)}
      onBlur={save} onKeyDown={e => e.key === 'Enter' && save()}
      className="h-6 text-xs px-1 w-full min-w-[60px]" />
  );
}

function AttendanceCell({ value, batchId, studentId, onSave }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const handleChange = async (newVal) => {
    setVal(newVal);
    await onSave(batchId, studentId, { attendance: newVal });
  };
  const colors = {
    Regular: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Absenteeism: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Select value={val || '__none__'} onValueChange={v => handleChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className={`h-6 text-[10px] rounded-sm min-w-[90px] border ${colors[val] || 'border-slate-200'}`} data-testid={`attendance-${studentId}`}>
        <SelectValue placeholder="-" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">-</SelectItem>
        <SelectItem value="Regular">Regular</SelectItem>
        <SelectItem value="Absenteeism">Absenteeism</SelectItem>
      </SelectContent>
    </Select>
  );
}

// Mock Interview status — dropdown that mirrors the admin PdpMockEditor
// (Completed / Scheduled / Not Done). Colour-coded so trainers can eyeball
// which students still need a mock at a glance.
function MockCell({ value, batchId, studentId, onSave }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const handleChange = async (newVal) => {
    setVal(newVal);
    await onSave(batchId, studentId, { mock_interview: newVal });
  };
  const colors = {
    Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
    'Not Done': 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <Select value={val || '__none__'} onValueChange={v => handleChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className={`h-6 text-[10px] rounded-sm min-w-[100px] border ${colors[val] || 'border-slate-200'}`} data-testid={`mock-${studentId}`}>
        <SelectValue placeholder="-" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">-</SelectItem>
        <SelectItem value="Completed">Completed</SelectItem>
        <SelectItem value="Scheduled">Scheduled</SelectItem>
        <SelectItem value="Not Done">Not Done</SelectItem>
      </SelectContent>
    </Select>
  );
}

function PlacementCheckbox({ value, batchId, studentId, onSave }) {
  const [checked, setChecked] = useState(!!value);
  useEffect(() => { setChecked(!!value); }, [value]);
  const handleToggle = async () => {
    const newVal = !checked;
    setChecked(newVal);
    await onSave(batchId, studentId, { ready_for_placement: newVal });
  };
  return (
    <button
      onClick={handleToggle}
      className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
        checked ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300 hover:border-slate-400'
      }`}
      data-testid={`placement-check-${studentId}`}
      title={checked ? 'Ready for placement' : 'Not ready'}
    >
      {checked && <Check className="w-3.5 h-3.5 text-white" />}
    </button>
  );
}

// ─── Batch Card (matching admin report exactly) ──────────────

function BatchCard({ batch, trainerName }) {
  const [expanded, setExpanded] = useState(false);
  const [students, setStudents] = useState([]);
  const [batchComment, setBatchComment] = useState(batch.comment || '');
  const [whatsappLinkCrm, setWhatsappLinkCrm] = useState(batch.whatsapp_link_crm || '');
  const [auditTrainerName, setAuditTrainerName] = useState(batch.audit_trainer_name || '');
  const [notesOfAuditor, setNotesOfAuditor] = useState(batch.notes_of_auditor || '');
  const [loading, setLoading] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const commentChanged = batchComment !== (batch.comment || '');

  const loadStudents = useCallback(async () => {
    if (students.length > 0) return;
    setLoading(true);
    try {
      const r = await api.get(`/trainer/batches/${batch.batch_id}/students`);
      const stArr = (r.data.students || []).map((s, i) => ({
        ...s,
        sno: i + 1,
        has_overdue: s.dues_status === 'overdue',
        has_partially_paid: s.dues_status === 'partially_paid',
      }));
      setStudents(stArr);
      setBatchComment(r.data.comment || '');
      setWhatsappLinkCrm(r.data.whatsapp_link_crm || '');
      setAuditTrainerName(r.data.audit_trainer_name || '');
      setNotesOfAuditor(r.data.notes_of_auditor || '');
    } catch { toast.error('Failed to load students'); }
    setLoading(false);
  }, [batch.batch_id, students.length]);

  const handleExpand = () => {
    if (!expanded) loadStudents();
    setExpanded(!expanded);
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await api.put(`/trainer/batches/${batch.batch_id}/comment`, { comment: batchComment });
      toast.success('Batch comment saved');
    } catch { toast.error('Failed to save'); }
    setSavingComment(false);
  };

  const handleSaveRemark = async (batchId, studentId, data) => {
    try {
      await api.put(`/trainer/batches/${batchId}/students/${studentId}/remarks`, data);
    } catch { toast.error('Failed to save remark'); }
  };

  const handleUpdateStudent = (studentId, field, newVal) => {
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, [field]: newVal } : s));
  };

  const saveAndUpdate = async (batchId, studentId, data) => {
    await handleSaveRemark(batchId, studentId, data);
    const field = Object.keys(data)[0];
    handleUpdateStudent(studentId, field, data[field]);
  };

  // Export columns for this batch
  const studentExportCols = [
    { key: 'sno', label: '#' },
    { key: 'enrollment_no', label: 'Enrollment No' },
    { key: 'name', label: 'Student Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'counsellor', label: 'Counsellor' },
    { key: 'sector', label: 'Sector' },
    { key: 'pdp', label: 'PDP' },
    { key: 'mock_interview', label: 'Mock Interview' },
    { key: 'mock_interview_link', label: 'Video / Transcript Link' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'ready_for_placement', label: 'Ready for Placement', render: s => s.ready_for_placement ? 'Yes' : 'No' },
    { key: 'dues_status', label: 'Dues Status', render: s => s.has_overdue ? 'Overdue' : s.has_partially_paid ? 'Partially Paid' : '-' },
  ];

  const exportFilename = `Batch_${batch.batch_number || batch.batch_id}_${batch.course_name || ''}`.replace(/\s/g, '_');

  return (
    <Card className="border overflow-hidden">
      {/* Batch Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50/70 cursor-pointer hover:bg-slate-100/70 transition-colors"
        onClick={handleExpand} data-testid={`report-batch-card-${batch.batch_id}`}>
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-slate-800">{batch.name || batch.course_name}</div>
          {batch.batch_number && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">#{batch.batch_number}</Badge>}
          <span className="text-[10px] text-slate-400">{batch.timing}</span>
          <span className="text-[10px] text-slate-400">{batch.start_date} {batch.end_date ? `→ ${batch.end_date}` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[9px]">{batch.student_count} students</Badge>
          <Badge variant="outline" className={`text-[9px] ${batch.status === 'Running' ? 'bg-emerald-50 text-emerald-700' : batch.status === 'Completed' ? 'bg-blue-50 text-blue-700' : batch.status === 'On Hold' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
            {batch.status}
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
          ) : (
            <>
              {/* Export Buttons */}
              {students.length > 0 && (
                <div className="flex items-center gap-2 px-4 pt-3 justify-end">
                  <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => exportToExcel(students, studentExportCols, exportFilename)} data-testid={`export-batch-excel-${batch.batch_id}`}>
                    <FileSpreadsheet className="w-3 h-3 mr-1" />Excel
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => exportToPdf(students, studentExportCols, `${trainerName} - Batch #${batch.batch_number || ''} ${batch.course_name || ''}`)} data-testid={`export-batch-pdf-${batch.batch_id}`}>
                    <FileText className="w-3 h-3 mr-1" />PDF
                  </Button>
                </div>
              )}

              {/* Student Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-cyan-50/50">
                      <TableHead className="text-[9px] uppercase w-8">S.No</TableHead>
                      <TableHead className="text-[9px] uppercase">Enroll No</TableHead>
                      <TableHead className="text-[9px] uppercase">Name</TableHead>
                      <TableHead className="text-[9px] uppercase">Phone</TableHead>
                      <TableHead className="text-[9px] uppercase">Email</TableHead>
                      <TableHead className="text-[9px] uppercase">Counsellor</TableHead>
                      <TableHead className="text-[9px] uppercase">Sector</TableHead>
                      <TableHead className="text-[9px] uppercase">PDP</TableHead>
                      <TableHead className="text-[9px] uppercase">Mock Int.</TableHead>
                      <TableHead className="text-[9px] uppercase" title="Public URL for the mock interview recording and/or transcript. Google Drive → Anyone with the link; YouTube → Unlisted.">Video / Transcript</TableHead>
                      <TableHead className="text-[9px] uppercase">Attendance</TableHead>
                      <TableHead className="text-[9px] uppercase">Ready for Placement</TableHead>
                      <TableHead className="text-[9px] uppercase text-right">Dues Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow><TableCell colSpan={13} className="text-center text-xs text-slate-400 py-6">No students enrolled</TableCell></TableRow>
                    ) : students.map(s => (
                      <TableRow key={s.student_id} className="hover:bg-slate-50/50">
                        <TableCell className="text-xs text-slate-400">{s.sno}</TableCell>
                        <TableCell className="text-xs font-medium text-violet-600">{s.enrollment_no}</TableCell>
                        <TableCell className="text-xs font-medium text-slate-800">{s.name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{s.phone}</TableCell>
                        <TableCell className="text-[10px] text-blue-600 max-w-[120px] truncate">{s.email}</TableCell>
                        <TableCell className="text-[10px] text-slate-500">{s.counsellor || '-'}</TableCell>
                        <TableCell><EditableCell value={s.sector} field="sector" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell><EditableCell value={s.pdp} field="pdp" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell><MockCell value={s.mock_interview} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} /></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 min-w-[120px]" data-testid={`mock-link-cell-${s.student_id}`}>
                            <div className="flex-1 min-w-0">
                              <EditableCell value={s.mock_interview_link} field="mock_interview_link" batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} />
                            </div>
                            {isSafeLink(s.mock_interview_link) && (
                              <a href={s.mock_interview_link} target="_blank" rel="noopener noreferrer"
                                className="shrink-0 text-violet-500 hover:text-violet-700"
                                title="Open link"
                                data-testid={`mock-link-open-${s.student_id}`}>
                                <Link2 className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <AttendanceCell value={s.attendance} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PlacementCheckbox value={s.ready_for_placement} batchId={batch.batch_id} studentId={s.student_id} onSave={saveAndUpdate} />
                        </TableCell>
                        <TableCell className="text-right">
                          {s.has_overdue ? (
                            <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200">Overdue</Badge>
                          ) : s.has_partially_paid ? (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Partially Paid</Badge>
                          ) : (
                            <span className="text-[10px] text-slate-300">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Batch Comment & WhatsApp / Audit — CRM fields are read-only */}
              <div className="border-t p-4 bg-slate-50/30 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Batch Overall Comment</Label>
                    <Textarea value={batchComment} onChange={e => setBatchComment(e.target.value)}
                      className="mt-1 text-xs min-h-[60px]" placeholder="Overall batch comments..." />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><Link2 className="w-3 h-3" /> WhatsApp Group Link</Label>
                    <div className="mt-1 relative" title="Read-only · synced from Zoho CRM">
                      <Input readOnly value={whatsappLinkCrm}
                        data-testid="tr-wa-crm-input"
                        className="text-xs h-8 bg-slate-100 text-slate-600 cursor-not-allowed pr-14"
                        placeholder={whatsappLinkCrm ? '' : 'Not set on CRM'} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span>
                    </div>
                    {whatsappLinkCrm && (
                      <a href={whatsappLinkCrm} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-violet-600 hover:underline mt-1 inline-block truncate max-w-full" data-testid="tr-wa-crm-open">
                        Open link ↗
                      </a>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><User className="w-3 h-3" /> Audit Trainer Name</Label>
                    <div className="mt-1 relative" title="Read-only · synced from Zoho CRM">
                      <Input readOnly value={auditTrainerName}
                        data-testid="tr-audit-trainer-input"
                        className="text-xs h-8 bg-slate-100 text-slate-600 cursor-not-allowed pr-14"
                        placeholder={auditTrainerName ? '' : 'Not set on CRM'} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-400 font-semibold flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Notes of Auditor</Label>
                    <div className="mt-1 relative" title="Read-only · synced from Zoho CRM">
                      <Textarea readOnly value={notesOfAuditor}
                        data-testid="tr-audit-notes-input"
                        className="text-xs min-h-[60px] bg-slate-100 text-slate-600 cursor-not-allowed pr-14"
                        placeholder={notesOfAuditor ? '' : 'Not set on CRM'} />
                      <span className="absolute right-2 top-2 text-[9px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">CRM</span>
                    </div>
                  </div>
                </div>
                {commentChanged && (
                  <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700 rounded-sm"
                    onClick={handleSaveComment} disabled={savingComment}>
                    <Save className="w-3 h-3 mr-1" />{savingComment ? 'Saving...' : 'Save Batch Info'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Main Report Page ────────────────────────────────────────

export default function TrainerReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('weekday');
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    api.get('/trainer/batches')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-12">Failed to load report</p>;

  const subTabs = [
    { id: 'weekday', label: 'Weekday', count: data.weekday_running.length },
    { id: 'weekend', label: 'Weekend', count: data.weekend_running.length },
    { id: 'completed', label: 'Completed Batches', count: data.completed.length },
    { id: 'upcoming', label: 'Upcoming', count: data.upcoming.length },
    { id: 'on_hold', label: 'On Hold', count: data.on_hold.length },
  ];

  const currentBatches = subTab === 'weekday' ? data.weekday_running
    : subTab === 'weekend' ? data.weekend_running
    : subTab === 'completed' ? data.completed
    : subTab === 'upcoming' ? data.upcoming
    : data.on_hold;

  // Prepare all-students export data from all batches
  const allBatches = [...data.weekday_running, ...data.weekend_running, ...data.completed, ...data.upcoming, ...data.on_hold];
  const batchSummaryColumns = [
    { key: 'batch_number', label: 'Batch #' },
    { key: 'course_name', label: 'Course' },
    { key: 'centre_name', label: 'Centre' },
    { key: 'batch_type', label: 'Type' },
    { key: 'start_date', label: 'Start Date' },
    { key: 'status', label: 'Status' },
    { key: 'student_count', label: 'Students' },
  ];

  return (
    <div className="space-y-4" data-testid="trainer-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">My Report</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {data.trainer_name} &middot; {data.total_batches} batch{data.total_batches !== 1 ? 'es' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm text-sky-700 border-sky-200 bg-sky-50 hover:bg-sky-100"
            onClick={() => setGuideOpen(true)}
            data-testid="trainer-open-guide"
            title="How to fill PDP, Mock Interview & Video / Transcript">
            <HelpCircle className="w-3.5 h-3.5 mr-1" />How to fill
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToExcel(allBatches, batchSummaryColumns, `${data.trainer_name}_Batch_Summary`)} data-testid="export-all-excel">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportToPdf(allBatches, batchSummaryColumns, `${data.trainer_name} - Batch Summary`)} data-testid="export-all-pdf">
            <FileText className="w-3.5 h-3.5 mr-1" />PDF
          </Button>
        </div>
      </div>

      <TrainerGuideModal open={guideOpen} onOpenChange={setGuideOpen} />

      {/* Sub-tabs: Weekday | Weekend | Completed | Upcoming | On Hold */}
      <div className="border-b flex gap-0">
        {subTabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${subTab === st.id ? 'border-cyan-500 text-cyan-700 bg-cyan-50/50' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid={`trainer-report-subtab-${st.id}`}>
            {st.label} <span className="ml-1 text-[10px] text-slate-400">({st.count})</span>
          </button>
        ))}
      </div>

      {currentBatches.length === 0 ? (
        <div className="text-center py-12"><p className="text-xs text-slate-400">No batches in this category</p></div>
      ) : (
        <div className="space-y-3">
          {currentBatches.map(b => <BatchCard key={b.batch_id} batch={b} trainerName={data.trainer_name} />)}
        </div>
      )}
    </div>
  );
}
